// src/screens/OrderLiveScreen.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import request from "../utils/request";
import { MapContainer, TileLayer, Marker, Polyline, Circle, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { createOrderSocket, bindOrderSocket } from "../store/thunks/trackingSocket";
import { selectDriverLoc, selectOrderedTimeline } from "../store/slices/orderTrackingSlices";

/* ---------------- helpers ---------------- */

const num = (v) => {
  const n = typeof v === "string" ? Number(v.trim()) : Number(v);
  return Number.isFinite(n) ? n : null;
};

const coerceLatLngFromAny = (obj) => {
  if (!obj) return null;

  // direct lat/lng
  const latA = num(obj.lat), lngA = num(obj.lng);
  if (Number.isFinite(latA) && Number.isFinite(lngA)) return { lat: latA, lng: lngA };

  // latitude/longitude
  const latB = num(obj.latitude), lngB = num(obj.longitude);
  if (Number.isFinite(latB) && Number.isFinite(lngB)) return { lat: latB, lng: lngB };

  // nested location object
  if (obj.location) {
    const nested = coerceLatLngFromAny(obj.location);
    if (nested) return nested;
    if (Array.isArray(obj.location.coordinates) && obj.location.coordinates.length >= 2) {
      const lng = num(obj.location.coordinates[0]);
      const lat = num(obj.location.coordinates[1]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }
  }

  // generic [lng, lat]
  if (Array.isArray(obj.coordinates) && obj.coordinates.length >= 2) {
    const lng = num(obj.coordinates[0]);
    const lat = num(obj.coordinates[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  // common container fallbacks
  if (obj.geo) {
    const g = coerceLatLngFromAny(obj.geo);
    if (g) return g;
  }
  if (obj.coords) {
    const c = coerceLatLngFromAny(obj.coords);
    if (c) return c;
  }

  return null;
};

const isSameLocalDay = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
};

const time12 = (isoOrTs) =>
  isoOrTs ? new Date(isoOrTs).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";

const ageSec = (tsLike) => {
  if (!tsLike) return Infinity;
  const t = typeof tsLike === "number" ? tsLike : new Date(tsLike).getTime();
  return Math.max(0, Math.floor((Date.now() - t) / 1000));
};

const fmtMiles = (mi) => {
  if (!Number.isFinite(mi)) return null;
  return mi >= 10 ? `${mi.toFixed(0)} mi` : `${mi.toFixed(1)} mi`;
};

const classNames = (...xs) => xs.filter(Boolean).join(" ");
const STALE_IDLE_S = 15 * 60; // 15m idle flag

// distance (miles)
const haversineMi = (a, b) => {
  if (!a || !b || !Number.isFinite(a.lat) || !Number.isFinite(a.lng) || !Number.isFinite(b.lat) || !Number.isFinite(b.lng)) return Infinity;
  const R = 3958.761;
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const makeEmojiIcon = (emoji, { size = 28, rotate = 0 } = {}) =>
  L.divIcon({
    html: `<div style="font-size:${size}px;line-height:${size}px;transform:rotate(${rotate}deg)">${emoji}</div>`,
    className: "emoji-icon",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });

const icons = {
  store: makeEmojiIcon("🛒", { size: 28 }),
  home: makeEmojiIcon("🏠", { size: 30 }),
  car: (heading = 0) => makeEmojiIcon("🚗", { size: 30, rotate: heading }),

  label: (text) =>
    L.divIcon({
      html: `
        <div style="position:relative;left:50%;top:-6px;transform:translate(-50%,-100%);pointer-events:none;">
          <div style="
            display:inline-flex;align-items:center;gap:6px;
            padding:6px 10px;border-radius:12px;
            background:#ffffffE6;border:1px solid #e5e7eb;
            box-shadow:0 8px 24px #0000001A, 0 1px 2px #00000014;
            backdrop-filter:saturate(140%) blur(8px);-webkit-backdrop-filter:saturate(140%) blur(8px);
            font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;
            font-size:12px;font-weight:600;letter-spacing:.2px;
            color:#0f172a;
          ">
            ${text}
          </div>
        </div>`.trim(),
      className: "label-icon",
      iconSize: [0, 0],
      iconAnchor: [0, 0],
    }),

  legLabel: (text) =>
    L.divIcon({
      html: `
        <div style="position:relative;left:50%;top:-8px;transform:translate(-50%,-100%);pointer-events:none;">
          <div style="
            display:inline-flex;align-items:center;
            padding:6px 12px;border-radius:14px;
            background:linear-gradient(180deg,#f59e0b 0%,#d97706 100%);
            border:1px solid #fdba74;
            box-shadow:0 10px 26px #00000029, 0 1px 2px #0000001F;
            font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;
            font-size:7px;font-weight:700;letter-spacing:.25px;
            color:#ffffff;text-shadow:0 1px 1px #00000059;
          ">
            ${text}
          </div>
        </div>`.trim(),
      className: "label-icon",
      iconSize: [0, 0],
      iconAnchor: [0, 0],
    }),
};

const PALETTE = [
  "#2563eb", "#16a34a", "#dc2626", "#9333ea", "#f59e0b", "#0ea5e9",
  "#ef4444", "#22c55e", "#f43f5e", "#a855f7", "#10b981", "#eab308",
  "#fb7185", "#3b82f6", "#14b8a6", "#f97316"
];
const hashIndex = (s) => {
  let h = 0; const str = String(s);
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % PALETTE.length;
};

const midpoint = (a, b) => (a && b ? { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 } : null);

const buildPropLabel = (obj) => {
  const l1 = obj?.address?.line1 || "";
  const l2 = obj?.address?.line2 || "";
  const lbl = `${l1}${l2 ? " " + l2 : ""}`.trim();
  return lbl || obj?.name || "Property";
};

const getStoreCoords = (o) => {
  const lat = num(o?.storeLat), lng = num(o?.storeLng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  if (o?.store) {
    const s = coerceLatLngFromAny(o.store);
    if (s) return s;
  }
  return null;
};

const getPropertyCoords = (o, propsById) => {
  const lat = num(o?.dropoffLat), lng = num(o?.dropoffLng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };

  const propId = String(o?.propertyID || o?.propertyId || o?.property || "");
  if (propId && propsById[propId]?.loc) return propsById[propId].loc;

  if (o?.property?.address) {
    const p = coerceLatLngFromAny(o.property.address);
    if (p) return p;
  }
  if (o?.property) {
    const p2 = coerceLatLngFromAny(o.property);
    if (p2) return p2;
  }
  if (o?.destination) {
    const d = coerceLatLngFromAny(o.destination);
    if (d) return d;
  }
  return null;
};

/* -------- one-time Fit control -------- */
function FitOnceControl({ points }) {
  const map = useMap();
  const didFitRef = useRef(false);
  const fit = useCallback(() => {
    const arr = points
      .filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng))
      .map((p) => [p.lat, p.lng]);
    if (arr.length >= 2) map.fitBounds(L.latLngBounds(arr), { padding: [44, 44] });
    else if (arr.length === 1) map.setView(arr[0], 13);
  }, [map, points]);
  useEffect(() => { if (!didFitRef.current) { didFitRef.current = true; fit(); } }, [fit]);
  return (
    <div
      onClick={() => fit()}
      className="absolute top-4 right-4 z-[500] cursor-pointer select-none px-3 py-2 rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.08)] border border-[#e5e7eb] bg-[#ffffff] text-[#111827] text-xs"
    >
      Fit
    </div>
  );
}

/* Ensure Leaflet recalculates after layout/resize */
function InvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    const on = () => map.invalidateSize();
    const t1 = setTimeout(on, 0);
    const t2 = setTimeout(on, 300);
    window.addEventListener("resize", on);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener("resize", on); };
  }, [map]);
  return null;
}

/* Focus map when a row is clicked */
function FocusOnSelection({ selection }) {
  const map = useMap();
  const prevId = useRef(null);
  useEffect(() => {
    if (!selection) return;
    if (prevId.current === selection.id) return;
    prevId.current = selection.id;
    const pts = [];
    if (selection.store) pts.push([selection.store.lat, selection.store.lng]);
    if (selection.drop) pts.push([selection.drop.lat, selection.drop.lng]);
    if (selection.dloc) pts.push([selection.dloc.lat, selection.dloc.lng]);
    if (pts.length > 0) map.fitBounds(L.latLngBounds(pts), { padding: [48, 48] });
  }, [selection, map]);
  return null;
}

/* ---------------- main screen ---------------- */

export default function OrderLiveScreen() {
  const dispatch = useDispatch();
  const state = useSelector((s) => s);
  const { token, adminInfo } = useSelector((s) => s.login || {});
  const canAuth = Boolean(adminInfo);

  const [orders, setOrders] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [openTimeline, setOpenTimeline] = useState({});

  const [driverNameById, setDriverNameById] = useState({});
  const [driverAddrById, setDriverAddrById] = useState({});
  const [propsById, setPropsById] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data = [] } = await request.get("/orders", {
          params: { status: "pending,requested,picked", limit: 50 },
        });
        if (cancelled) return;
        const todays = data.filter((o) => isSameLocalDay(o?.inStockDate));
        setOrders(todays);
      } catch (e) {
        console.error("Failed to load orders:", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data = [] } = await request.get("/drivers");
        if (cancelled) return;
        const names = {};
        const addrs = {};
        data.forEach((d) => {
          const id = String(d._id);
          const nm = `${d.firstName ?? ""} ${d.lastName ?? ""}`.trim() || d.email || "Unnamed Driver";
          names[id] = nm;
          const ca = d.currentAddress;
          if (ca && Number.isFinite(ca.lat) && Number.isFinite(ca.lng)) {
            addrs[id] = { lat: Number(ca.lat), lng: Number(ca.lng), ts: ca.ts || null };
          }
        });
        setDriverNameById(names);
        setDriverAddrById(addrs);
      } catch (e) {
        console.error("Failed to load drivers:", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data = [] } = await request.get("/properties", { params: { limit: 1000 } });
        if (cancelled) return;
        const byId = {};
        data.forEach((p) => {
          const pid = String(p._id || p.id);
          const label = buildPropLabel(p);
          const loc =
            coerceLatLngFromAny(p?.address) ||
            coerceLatLngFromAny(p?.location) ||
            coerceLatLngFromAny(p?.geo) ||
            coerceLatLngFromAny(p);
          byId[pid] = { label, loc: loc || null };
        });
        setPropsById(byId);
      } catch (e) {
        console.error("Failed to load properties:", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const socketsRef = useRef(new Map());
  useEffect(() => {
    const pool = socketsRef.current;
    if (orders.length === 0) { return; }
    const want = new Set(orders.map((o) => String(o._id)));
    for (const id of want) {
      if (pool.has(id)) continue;
      const socket = createOrderSocket({ token, orderId: id });
      const unbind = bindOrderSocket(dispatch, socket, id);
      socket.onclose = () => { try { unbind(); } catch {}; pool.delete(id); };
      pool.set(id, socket);
    }
    for (const [id, s] of Array.from(pool.entries())) {
      if (!want.has(id)) {
        try { s.close(); } catch {}
        pool.delete(id);
      }
    }
    return () => {
      for (const [, s] of pool) try { s.close(); } catch {}
      pool.clear();
    };
  }, [ dispatch, token, orders]);

  const sorted = useMemo(() => {
    const now = Date.now();
    return [...orders].sort((a, b) => {
      const aSoon = a.storeArrivalTime ? Math.abs(new Date(a.storeArrivalTime).getTime() - now) : Number.MAX_SAFE_INTEGER;
      const bSoon = b.storeArrivalTime ? Math.abs(new Date(b.storeArrivalTime).getTime() - now) : Number.MAX_SAFE_INTEGER;
      return aSoon - bSoon;
    });
  }, [orders]);

  const driverNameForOrder = (o) => {
    const id = o?.driverID || o?.driverId || o?.assignee || null;
    if (id && driverNameById[String(id)]) return driverNameById[String(id)];
    if (typeof o.driverName === "string" && o.driverName.trim()) return o.driverName.trim();
    return "Not Assigned";
  };

  const propertyLabelForOrder = (o) => {
    const propId = String(o.propertyID || o.propertyId || o.property || "");
    if (propId && propsById[propId]?.label) return propsById[propId].label;
    return buildPropLabel(o?.property || o?.Property || {});
  };

  const allMapData = useMemo(() => {
    return sorted.map((o) => {
      const id = String(o._id);
      const color = PALETTE[hashIndex(id)];
      const unique = o.uniqueId || id.slice(-6);

      const store = getStoreCoords(o);
      const propId = String(o.propertyID || o.propertyId || o.property || "");
      const propRec = propId ? propsById[propId] : undefined;
      const drop = getPropertyCoords(o, propsById);

      const live = selectDriverLoc(state, id);
      const did = String(o.driverID || o.driverId || o.assignee || "");
      const fallback = driverAddrById[did] || null;
      const dloc = live && Number.isFinite(live.lat) && Number.isFinite(live.lng) ? live : fallback;

      const tl = selectOrderedTimeline(state, id) || [];
      const hit = (t) => tl.some((e) => e?.type === t && e?.ts);
      const leftStore = hit("leftStore");

      const toStore = !leftStore;
      const target = toStore ? store : drop;

      const driverName = driverNameForOrder(o);
      const propLabel = propRec?.label || propertyLabelForOrder(o);

      return { id, unique, color, store, drop, dloc, toStore, target, driverName, propLabel, order: o };
    });
  }, [sorted, state, driverAddrById, driverNameById, propsById]);

  const fitPoints = useMemo(() => {
    const pts = [];
    for (const m of allMapData) {
      if (m.store) pts.push(m.store);
      if (m.drop) pts.push(m.drop);
      if (m.dloc) pts.push(m.dloc);
    }
    return pts;
  }, [allMapData]);

  const activeSelection = useMemo(
    () => (activeId ? allMapData.find((m) => m.id === activeId) || null : null),
    [activeId, allMapData]
  );

  const Styles = () => (
    <style>{`
      .dash-anim { stroke-dasharray: 6 8; animation: dashMove 1.8s linear infinite; }
      @keyframes dashMove { to { stroke-dashoffset: -28; } }
    `}</style>
  );

  return (
    <div className="w-full h-[calc(100vh-72px)] bg-[#f8fafc] p-4 md:p-6">
      <Styles />

      {/* FLEX SPLIT: left narrow list, right large map */}
      <div className="h-full flex gap-4">
        {/* Left list (fixed narrow width) */}
        <div className="flex-3 min-h-0 overflow-auto p-3 space-y-3">
    {sorted.map((o) => {
      const id = String(o._id);
      const driverName = driverNameForOrder(o);
      const dloc = selectDriverLoc(state, id);
      const sec = dloc?.ts ? ageSec(dloc.ts) : Infinity;
      const idle = sec >= STALE_IDLE_S;
      const soon =
        o.storeArrivalTime &&
        Math.abs(new Date(o.storeArrivalTime).getTime() - Date.now()) <= 60 * 60 * 1000;

            const hasDriver = Boolean(o.driverID || o.driverId || o.assignee || o.driverName);
            const hasPing = sec !== Infinity;
            const arrivalMissing = !o.storeArrivalTime;

            const dropOrStore = o.dropoffLat || o.dropoffLng || o.property || o.store ? getPropertyCoords(o, propsById) || getStoreCoords(o) : null;
            const target = dropOrStore || null;
            const distMi = dloc && target ? haversineMi(dloc, target) : null;
            const distText = fmtMiles(distMi);

      const tl = selectOrderedTimeline(state, id) || [];
      const last = tl.length ? tl[tl.length - 1] : null;
      const lastText = last ? (last.label || last.type) : null;
      const lastTime = last?.ts ? time12(last.ts) : null;

      // avatar initials for driver (no external deps)
      const initials = (driverName || "N A")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase())
        .join("");

      return (
        <div
          key={id}
          onClick={() => setActiveId((cur) => (cur === id ? null : id))}
          role="button"
          className={classNames(
            "rounded-2xl border border-[#e5e7eb] bg-[#ffffff] px-3.5 py-3 cursor-pointer transition",
            "hover:shadow-sm",
            activeId === id ? "shadow-md ring-1 ring-[#c7d2fe]" : "",
            soon ? "ring-1 ring-[#a7f3d0]" : ""
          )}
          title={activeId === id ? "Click to unselect" : "Click to highlight on map"}
        >
              {/* Row: chips and status */}
              <div className="flex items-center gap-2 mb-2">
                <span
                  className={classNames(
                    "px-2.5 py-1 rounded-md text-[11px] font-semibold border shrink-0",
                    activeId === id ? "bg-[#eef2ff] text-[#3730a3] border-[#c7d2fe]" : "bg-[#f4f6f8] text-[#111827] border-[#e5e7ea]"
                  )}
                >
                  {o.uniqueId || id.slice(-6)}
                </span>
                <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold border border-[#e2e8f0] bg-[#f8fafc] text-[#0f172a] capitalize">
                  {o.status || "requested"}
                </span>
                {o.storeArrivalTime && (
                  <span className="px-2 py-0.5 rounded-md text-[11px] border border-[#e2e8f0] bg-[#f8fafc] text-[#0f172a]">
                    Arrive {time12(o.storeArrivalTime)}
                  </span>
                )}
              </div>

              {/* Main row: driver + property + telemetry */}
              <div className="flex items-start justify-between gap-3 min-w-0">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-7 h-7 rounded-full bg-[#eef2ff] text-[#3730a3] border border-[#c7d2fe] flex items-center justify-center text-[11px] font-semibold shrink-0"
                      aria-hidden="true"
                      title={driverName || "Not Assigned"}
                    >
                      {initials || "NA"}
                    </div>
                    <div className="min-w-0">
                      <div
                        className="text-[13px] font-semibold leading-5 text-[#0f172a] truncate"
                        title={driverName || "Not Assigned"}
                      >
                        {driverName || "Not Assigned"}
                      </div>
                      <div
                        className="text-[12px] text-[#475569] leading-5 truncate"
                        title={propertyLabelForOrder(o)}
                      >
                        {propertyLabelForOrder(o)}
                      </div>
                    </div>
                  </div>

                  {/* Signals */}
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {!hasDriver && (
                      <span className="px-2 py-0.5 rounded-full border bg-[#fff1f2] border-[#fecdd3] text-[#9f1239] text-[11px]">
                        No driver
                      </span>
                    )}
                    {!hasPing && (
                      <span className="px-2 py-0.5 rounded-full border bg-[#fff7ed] border-[#ffedd5] text-[#9a3412] text-[11px]">
                        No ping
                      </span>
                    )}
                    {idle && (
                      <span className="px-2 py-0.5 rounded-full border bg-[#fff7ed] border-[#ffedd5] text-[#9a3412] text-[11px]">
                        Idle 15m
                      </span>
                    )}
                    {arrivalMissing && (
                      <span className="px-2 py-0.5 rounded-full border bg-[#eef2ff] border-[#e0e7ff] text-[#3730a3] text-[11px]">
                        No window
                      </span>
                    )}
                    {soon && (
                      <span className="px-2 py-0.5 rounded-full border bg-[#ecfdf3] border-[#bbf7d0] text-[#166534] text-[11px]">
                        Window soon
                      </span>
                    )}
                  </div>

                  {/* Last update + distance */}
                  <div className="mt-2 flex items-center gap-2 text-[12px] text-[#334155]">
                    <span className="px-2 py-0.5 rounded-full bg-[#f8fafc] border border-[#e2e8f0] text-[11px] text-[#0f172a]">
                      {sec === Infinity
                        ? "no ping"
                        : sec >= 60
                        ? `${Math.floor(sec / 60)}m ${String(sec % 60).padStart(2, "0")}s`
                        : `${sec}s`}
                    </span>
                    {distText && (
                      <span className="px-2 py-0.5 rounded-full bg-[#f8fafc] border border-[#e2e8f0] text-[11px] text-[#0f172a]">
                        {distText} to {dropOrStore ? "dest" : "target"}
                      </span>
                    )}
                    {lastText && (
                      <span className="truncate text-[12px] text-[#475569]" title={lastText || ""}>
                        Last: {lastText}
                      </span>
                    )}
                    {lastTime && <span className="text-[11px] text-[#64748b] shrink-0">{lastTime}</span>}
                  </div>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenTimeline((m) => ({ ...m, [id]: !m[id] }));
                  }}
                  className="h-8 px-3 py-1 rounded-md border border-[#e5e7eb] hover:bg-[#f8fafc] text-[11px] text-[#0f172a] shrink-0"
                >
                  {openTimeline[id] ? "Hide" : "Timeline"}
                </button>
              </div>

              {/* Collapsible timeline */}
              {openTimeline[id] && <RowTimeline orderId={id} />}
        </div>
      );
    })}

    {sorted.length === 0 && (
      <div className="text-sm text-[#6b7280] px-2 py-3">
        No pending or requested orders for today.
      </div>
    )}
  </div>
        {/* Right: large map panel */}
        <div className="relative flex-1 min-w-0 h-full rounded-2xl overflow-hidden border border-[#e5e7eb] bg-white shadow-[0_10px_30px_rgba(0,0,0,0.08)]">
          <MapContainer
            className="absolute inset-0"
            center={[32.7157, -117.1611]}
            zoom={11}
            scrollWheelZoom
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

            {allMapData.map((o) => {
              const emphasize = activeId && activeId === o.id;
              const weightBase = emphasize ? 6 : 4;
              const dashWeight = emphasize ? 5 : 3;

              const baseHas = o.store && o.drop;
              const legHas = o.dloc && o.target;

              const tooFarFromStore = o.dloc && o.store ? haversineMi(o.dloc, o.store) > 50 : false;

              const baseMid = baseHas ? midpoint(o.store, o.drop) : null;
              const legMid = legHas ? midpoint(o.dloc, o.target) : null;

              return (
                <React.Fragment key={o.id}>
                  {o.store && (
                    <Marker position={[o.store.lat, o.store.lng]} icon={icons.store}>
                      <Tooltip direction="top" offset={[0, -8]} opacity={0.9}>
                        <div className="text-[11px]">
                          <div><b>Store</b></div>
                          <div>Order {o.unique}</div>
                        </div>
                      </Tooltip>
                    </Marker>
                  )}

                  {o.drop && (
                    <Marker position={[o.drop.lat, o.drop.lng]} icon={icons.home}>
                      <Tooltip direction="top" offset={[0, -8]} opacity={0.9}>
                        <div className="text-[11px]">
                          <div><b>Property</b></div>
                          <div>{o.propLabel}</div>
                        </div>
                      </Tooltip>
                    </Marker>
                  )}

                  {o.dloc && (
                    <>
                      <Marker key={`car-${o.id}-${o.dloc?.ts || 0}-${o.dloc?.heading || 0}`} position={[o.dloc.lat, o.dloc.lng]} icon={icons.car(o.dloc.heading || 0)}>
                        <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
                          <div className="text-[11px]">
                            <div><b>{o.driverName}</b></div>
                            <div>Order {o.unique}</div>
                          </div>
                        </Tooltip>
                      </Marker>
                      <Circle center={[o.dloc.lat, o.dloc.lng]} radius={40} pathOptions={{ color: "#22c55e", opacity: 0.35 }} />
                    </>
                  )}

                  {baseHas && (
                    <Polyline
                      positions={[[o.store.lat, o.store.lng], [o.drop.lat, o.drop.lng]]}
                      pathOptions={{ color: o.color, weight: weightBase, opacity: 0.88 }}
                    />
                  )}

                  {legHas && !tooFarFromStore && (
                    <Polyline
                      positions={[[o.dloc.lat, o.dloc.lng], [o.target.lat, o.target.lng]]}
                      pathOptions={{ color: "#111827", weight: dashWeight, className: "dash-anim" }}
                    />
                  )}

                  {legHas && legMid && !tooFarFromStore && (
                    <Marker
                      position={[legMid.lat, legMid.lng]}
                      icon={icons.legLabel(o.toStore ? "On Way To Store" : " ")}
                      interactive={false}
                    />
                  )}
                </React.Fragment>
              );
            })}

            <FocusOnSelection selection={activeSelection} />
            <FitOnceControl points={fitPoints} />
            <InvalidateOnResize />
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Collapsible row timeline ---------------- */

function RowTimeline({ orderId }) {
  const tl = useSelector((s) => selectOrderedTimeline(s, String(orderId))) || [];
  return (
    <div className="mt-2 bg-[#f8fafc] border border-[#e5e7eb] rounded-2xl p-3">
      <div className="text-xs font-medium text-[#0f172a] mb-2">Status timeline</div>
      <div className="space-y-2">
        {tl.length === 0 && <div className="text-xs text-[#6b7280]">No events yet.</div>}
        {tl.map((item) => {
          const hit = Boolean(item?.ts);
          return (
            <div
              key={item.key || item.type}
              className={`flex items-center justify-between p-2.5 rounded-xl border ${
                hit ? "bg-[#ffffff] border-[#e5e7eb]" : "bg-[#f1f5f9] border-[#e2e8f0] opacity-80"
              }`}
            >
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    hit ? (item.source === "button" ? "bg-[#fb923c]" : "bg-[#22c55e]") : "bg-[#cbd5e1]"
                  }`}
                />
                <span className="text-xs text-[#111827]">{item.label || item.type}</span>
                {hit && (
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full border ${
                      item.source === "button"
                        ? "bg-[#fff7ed] border-[#fdba74] text-[#9a3412]"
                        : "bg-[#ecfdf5] border-[#6ee7b7] text-[#065f46]"
                    }`}
                  >
                    {item.source === "button" ? "button" : "geo"}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-[#6b7280]">{hit ? time12(item.ts) : ""}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
